export interface Project {
  id: number;
  title: string;
  subTitle: string;
  type: string;
  architect: string;
  status: string;
  client: string;
  projectValue: string;
  expertise: string;
  expertiseId?: number;
  parentExpertiseId?: number | null;
  parentExpertise?: string | null;
  description: string;
  floorArea: string;
  location: string;
  imageName: string;
  orderNr?: number;
  projectPartList: ProjectPart[];
  galleries?: Gallery[];
}

export type GalleryItemType = 'IMAGE' | 'VIDEO' | 'TEXT';
export type GalleryDisplaySize = 'standard' | 'wide' | 'tall' | 'feature' | 'full';
export type GalleryCaptionMode = 'overlay' | 'below' | 'hidden';
export type GalleryCanvasAlign = 'left' | 'center' | 'right';
export type GalleryAspectRatio = 'landscape' | 'portrait' | 'square' | 'cinematic';
export type GalleryTextFontFamily = 'inter' | 'serif-editorial' | 'mono-detail';
export type GalleryTextSize = 'body' | 'lead' | 'statement' | 'quote';
export type GalleryTextWeight = 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
export type GalleryTextAlign = 'left' | 'center' | 'right';
export type GalleryTextMaxWidth = 'narrow' | 'standard' | 'wide' | 'full';
export type GalleryTextTheme = 'light' | 'warm' | 'dark' | 'metal';
export type GalleryTextAccent = 'none' | 'lime-rule' | 'left-rule' | 'top-rule' | 'quote-mark';
export type GalleryTextAnimation = 'none' | 'fade-up' | 'reveal-line' | 'soft-blur';
export type GalleryTextColumns = 'one' | 'two';
export type GalleryMasonryGap =
  | 'none'
  | 'hairline'
  | 'tight'
  | 'compact'
  | 'standard'
  | 'relaxed'
  | 'generous'
  | 'roomy';
export type GalleryMasonryRhythm = 'editorial' | 'balanced' | 'hero';
export type GalleryMasonryFrame = 'edge' | 'soft' | 'museum';
export type GalleryMasonryHover = 'zoom' | 'lift' | 'still';
export type GalleryStoryRailComposition =
  | 'auto'
  | 'feature-offset'
  | 'side-by-side'
  | 'full-reveal'
  | 'full-parallax'
  | 'text-break';

export const GALLERY_TEXT_DEFAULTS = {
  textFontFamily: 'inter' as GalleryTextFontFamily,
  textSize: 'lead' as GalleryTextSize,
  textWeight: 'regular' as GalleryTextWeight,
  textItalic: false,
  textAlign: 'left' as GalleryTextAlign,
  textMaxWidth: 'standard' as GalleryTextMaxWidth,
  textTheme: 'light' as GalleryTextTheme,
  textAccent: 'none' as GalleryTextAccent,
  textAnimation: 'fade-up' as GalleryTextAnimation,
  textColumns: 'one' as GalleryTextColumns,
  textDropCap: false,
};

export interface GalleryImage {
  id?: number;
  name?: string;
  mediaName?: string;
  displayOrder: number;
  mediaType?: GalleryItemType;
  caption?: string;
  eyebrow?: string;
  title?: string;
  body?: string;
  posterName?: string;
  alt?: string;
  aspectRatio?: GalleryAspectRatio;
  imageWidth?: number | null;
  imageHeight?: number | null;
  displaySize?: GalleryDisplaySize;
  canvasAlign?: GalleryCanvasAlign;
  captionMode?: GalleryCaptionMode;
  isFeatured?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  storyRailComposition?: GalleryStoryRailComposition;
  credit?: string;
  /** Tiny data URL or image URL shown while the lightbox image loads. */
  placeholderDataUrl?: string;
  /** Compact BlurHash string for instant blurred preview while loading. */
  blurHash?: string;
}

export type GalleryLayout = 'MASONRY' | 'FILMSTRIP' | 'SHOWCASE' | 'TEXT_BLOCK';

export interface Gallery {
  id?: number;
  layout: GalleryLayout;
  title?: string;
  intro?: string;
  ctaLabel?: string;
  /** When true, lightbox shows a bottom thumbnail strip. When omitted, auto-enables for 9+ items. */
  showLightboxThumbnails?: boolean;
  /** Optional hex/css color for lightbox progress bar and active dot. */
  lightboxAccentColor?: string;
  /** When true, a "View Gallery" button appears below the hero on the project details page. */
  showViewGalleryButton?: boolean;
  /** Masonry gallery gap spacing. */
  masonryGap?: GalleryMasonryGap;
  /** Masonry tile composition preset. */
  masonryRhythm?: GalleryMasonryRhythm;
  /** Masonry frame and surface treatment. */
  masonryFrame?: GalleryMasonryFrame;
  /** Masonry hover interaction style. */
  masonryHover?: GalleryMasonryHover;
  /** When true, the filmstrip slider auto-advances between images. */
  autoplay?: boolean;
  /** When true, filmstrip renders AKT-style split composition with a supporting companion image panel. */
  filmstripSplitMode?: boolean;
  /** Interval in ms between auto-advances. Defaults to 4500. */
  autoplayInterval?: number;
  displayOrder: number;
  textContent?: string;
  textFontFamily?: GalleryTextFontFamily;
  textSize?: GalleryTextSize;
  textWeight?: GalleryTextWeight;
  textItalic?: boolean;
  textAlign?: GalleryTextAlign;
  textMaxWidth?: GalleryTextMaxWidth;
  textTheme?: GalleryTextTheme;
  textAccent?: GalleryTextAccent;
  textAnimation?: GalleryTextAnimation;
  textColumns?: GalleryTextColumns;
  textDropCap?: boolean;
  items: GalleryImage[];
}

/** Pre-computed view model for gallery items — avoids repeated function calls in templates */
export interface GalleryItemView extends GalleryImage {
  _trackKey: string;
  _isText: boolean;
  _isVideo: boolean;
  _mediaUrl: string;
  _posterUrl: string | null;
  _imageSrc: string;
  _thumbSrc: string;
  _lightboxSrc: string;
  _imageSrcSet: string;
  _thumbSrcSet: string;
  _lightboxSrcSet: string;
  _displaySize: GalleryDisplaySize;
  _canvasAlign: GalleryCanvasAlign;
  _captionMode: GalleryCaptionMode;
  _cellClass?: string;
  _placeholderSrc?: string;
}

export interface GalleryViewModel {
  items: GalleryItemView[];
  imageCount: number;
  videoCount: number;
  textCount: number;
}

interface ProjectPart {
  fileType: string;
  fileName: string;
  orderNr: number;
  description: string;
  youtubeUrl: string;
  posterName?: string;
}

export interface ProjectState {}

export interface SelectionOptions {
  types: string[];
  expertises: string[];
  locations: string[];
  statuses: string[];
}
